'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Skeleton, Typography, Button, Alert } from '@mui/material';
import {
	type EnhancedConversionFields,
	buildEnhancedConversionFields,
	isEnhancedConversionsEnabled,
	resolveGoogleClickIds,
} from '@aspendental/web-shared-utils';

import { useAppContext } from '@/context/AppContextProvider.ctx';
import { TAGBrand } from '@/types';
import { useConvertFlowUserDetailsValidation } from '@/hooks/useConvertFlowUserDetailsValidation/useConvertFlowUserDetailsValidation';
import postFormSubmit from '@/services/postFormSubmit';
import putLeadMetadata from '@/services/putLeadMetadata';
import { submitConsentData } from '@/services/submitConsentData';
import type { ConvertFlowQuiz as ConvertFlowQuizEntry } from '@/types/generated';
import { dataLayerConfig } from '@/utils/dataLayerConfig';
import { getMarketingSourceCookie, SEND_GBRAID_FEATURE, withoutBraidParams } from '@/utils/marketingSource';
import { logError } from '@/utils/miscUtils';
import { readSubmitFields } from '@/utils/convertFlowFieldValidators/convertFlowFieldValidators';

import {
	buildLeadAnswers,
	buildMarketingDataPayload,
	buildQuestionTextMap,
	buildSubmitFormPayload,
	collectMultiSelectSlugs,
	collectSelectedQuizFields,
	extractBackendFieldErrors,
	isQuizQuestionVisible,
	isSubmitFormSuccess,
	remapAnswerKeys,
	separateCFFields,
	setPathfinderReferralCookie,
} from './ConvertFlowQuiz.helpers';

// ConvertFlow form_id values that identify Pathfinder funnels.
// The form_id is a hidden field configured in the CF CTA builder and arrives
// via cfSubmit fields. Only quizzes with these IDs get the Pathfinder header.
const PATHFINDER_FORM_IDS = new Set(['36']);

// ---------------------------------------------------------------------------
// Script loader — ensures we only inject the CF script once globally.
// Matches the official ConvertFlow embed pattern: checks window.convertflow
// first and appends the script to document.body.
// ---------------------------------------------------------------------------

// ConvertFlowQuiz is always a ClearChoice funnel (posts to CLEARCHOICE_API_URL
// regardless of the ambient app brand), so analytics must report clearchoice.
// Reading the ambient app brand from context caused a mid-flow brand flip that
// also reset the funnel's accumulated state via the event effect's deps.
const QUIZ_BRAND = TAGBrand.ClearChoice;

const CF_SCRIPT_SRC = process.env.NEXT_PUBLIC_CONVERTFLOW_SCRIPT_SRC || 'https://js.convertflow.co/production/websites';
const scriptLoadPromises = new Map<string, Promise<void>>();
const injectedScriptEls = new Map<string, HTMLScriptElement>();

// cfCompletion is dispatched on window; ConvertFlow fires it more than once per
// submission, and every mounted instance (plus any stale HMR listener) observes
// each event. Dedupe at module scope, keyed by ctaId within a short window, so
// the "Submit Quiz" push (and convertFlowQuizComplete dispatch) happens once per
// real completion no matter how many listeners are attached — a per-instance ref
// can't dedupe across listeners.
const COMPLETION_DEDUPE_MS = 2000;
const lastCompletionAtByCta = new Map<number, number>();

/** Test-only: clears the cross-instance completion dedupe state. */
export function __resetCompletionDedupeForTests() {
	lastCompletionAtByCta.clear();
}

// ---------------------------------------------------------------------------
// Debug logging — OFF by default. Enable without a rebuild by running
//   localStorage.cfDebug = 'true'
// in the browser console and reloading, or set NEXT_PUBLIC_CONVERTFLOW_DEBUG=true.
// Used to diagnose which ConvertFlow window events actually fire per question
// (cfAnswer is known to fire inconsistently across CF widget types).
// ---------------------------------------------------------------------------

const CF_DEBUG: boolean = (() => {
	if (process.env.NEXT_PUBLIC_CONVERTFLOW_DEBUG === 'true') return true;
	try {
		return globalThis.window !== undefined && globalThis.window.localStorage.getItem('cfDebug') === 'true';
	} catch {
		return false;
	}
})();

function cfLog(label: string, data?: unknown): void {
	if (!CF_DEBUG) return;
	const t = typeof performance === 'object' ? `${performance.now().toFixed(0)}ms` : '';
	// eslint-disable-next-line no-console
	console.log(`%c[ConvertFlowQuiz] ${t} ${label}`, 'color:#7c3aed;font-weight:bold;', data ?? '');
}

function loadConvertFlowScript(websiteId: string): Promise<void> {
	// If ConvertFlow was already loaded externally (e.g. GTM), resolve immediately
	if (window.convertflow) {
		return Promise.resolve();
	}

	const existing = scriptLoadPromises.get(websiteId);
	if (existing) return existing;

	const promise = new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		script.src = `${CF_SCRIPT_SRC}/${websiteId}.js`;
		script.async = true;
		script.onerror = () => {
			// Clean up the failed script element so retries don't leave duplicates
			const el = injectedScriptEls.get(websiteId);
			if (el) {
				el.remove();
				injectedScriptEls.delete(websiteId);
			}
			scriptLoadPromises.delete(websiteId);
			reject(new Error('Failed to load ConvertFlow script'));
		};
		script.onload = () => resolve();
		injectedScriptEls.set(websiteId, script);
		// Append to body to match the official ConvertFlow embed pattern
		document.body.appendChild(script);
	});

	scriptLoadPromises.set(websiteId, promise);
	return promise;
}

// ---------------------------------------------------------------------------
// Sticky-header offset — measures whatever is pinned to the top of the viewport
// (sticky/fixed nav, announcement bar, etc.) so we can scroll a new quiz step
// below it instead of behind it.
// ---------------------------------------------------------------------------

function getStickyHeaderOffset(): number {
	if (typeof document.elementsFromPoint !== 'function') return 0;
	const x = Math.floor(window.innerWidth / 2);
	let offset = 0;
	// Sample a couple of points near the top so we don't miss a thin top bar.
	for (const y of [1, 12]) {
		for (const el of document.elementsFromPoint(x, y)) {
			const cs = window.getComputedStyle(el);
			if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
			const rect = el.getBoundingClientRect();
			// Only count elements actually pinned at the top edge.
			if (rect.top <= 1 && rect.bottom > offset) offset = rect.bottom;
		}
	}
	return offset;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function QuizSkeleton({ message }: { message: string }) {
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				p: 3,
				borderRadius: 2,
				bgcolor: 'grey.50',
				minHeight: 320,
			}}
		>
			{/* Fake "question" heading */}
			<Skeleton variant="text" width="60%" height={32} />

			{/* Fake answer option cards */}
			{[1, 2, 3, 4].map((i) => (
				<Skeleton key={i} variant="rounded" width="100%" height={48} sx={{ borderRadius: 1 }} />
			))}

			{/* Progress bar placeholder */}
			<Box sx={{ mt: 'auto' }}>
				<Skeleton variant="rounded" width="100%" height={8} />
			</Box>

			<Typography variant="bodyMediumBook" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
				{message}
			</Typography>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Error / fallback state
// ---------------------------------------------------------------------------

function QuizFallback({ fallbackUrl, fallbackLabel }: { fallbackUrl?: string; fallbackLabel?: string }) {
	return (
		<Alert
			severity="info"
			sx={{ borderRadius: 2 }}
			action={
				fallbackUrl ? (
					<Button color="inherit" size="small" href={fallbackUrl} target="_blank" rel="noopener noreferrer">
						{fallbackLabel || 'Take the quiz'}
					</Button>
				) : undefined
			}
		>
			The quiz couldn&apos;t be loaded right now.
			{fallbackUrl ? ' You can still take it using the link.' : ''}
		</Alert>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type QuizState = 'loading' | 'ready' | 'error';

interface CFEventCta {
	name: string;
	cta_type: string;
	id: number;
}

interface CFConversionDetail {
	event_type: string;
	cta: CFEventCta;
	variant: string;
	step: number;
	step_name: string | null;
	element: 'quiz' | 'form';
	button_text?: string;
	fields: Record<string, string | Record<string, string>> | null;
}

interface CFAnswerDetail {
	event_type: string;
	cta: CFEventCta;
	step: number;
	step_name: string | null;
	element: string;
	fields: {
		extra?: Record<string, string>;
	} | null;
}

interface CFCompletionDetail {
	event_type: string;
	cta?: CFEventCta;
}

interface CFViewDetail {
	event_type: string;
	cta: CFEventCta;
	step?: number;
	step_name?: string | null;
}

export type ConvertFlowQuizProps = ConvertFlowQuizEntry;

/** Attribution is never worth failing a lead over, so a resolver error just omits the ids. */
function resolveClickIdsSafely() {
	try {
		return resolveGoogleClickIds();
	} catch {
		return undefined;
	}
}

export function ConvertFlowQuiz({
	ctaId,
	loadingMessage,
	fallbackUrl,
	fallbackLabel,
	timeoutMs,
	websiteId,
	enableUserDetailsValidation,
	isSpanish,
}: ConvertFlowQuizProps) {
	const { environment, config } = useAppContext();
	const resolvedLoadingMessage = loadingMessage ?? 'Loading your personalized quiz…';
	const resolvedTimeoutMs = timeoutMs ?? 10_000;

	const containerRef = useRef<HTMLDivElement | null>(null);
	const [state, setState] = useState<QuizState>('loading');

	const baseUrl = useMemo(
		() =>
			environment === 'prod'
				? config.services.prod.CLEARCHOICE_API_URL
				: config.services.nonprod.CLEARCHOICE_API_URL,
		[environment, config]
	);

	const beffeGraphqlUrl = useMemo(
		() =>
			environment === 'prod'
				? (config.services.prod as { BEFFE_GRAPHQL_URL?: string })?.BEFFE_GRAPHQL_URL
				: (config.services.nonprod as { BEFFE_GRAPHQL_URL?: string })?.BEFFE_GRAPHQL_URL,
		[environment, config]
	);
	const apiHeaders = useMemo(
		() => ({
			'x-api-key': process.env.NEXT_PUBLIC_CC_API_KEY ?? '',
			'x-api-sig': process.env.NEXT_PUBLIC_CC_API_SIG ?? '',
			'Content-Type': 'application/json',
		}),
		[]
	);

	const formIdRef = useRef('');

	const buildApiHeaders = useCallback(
		() => ({
			...apiHeaders,
			...(PATHFINDER_FORM_IDS.has(formIdRef.current) && {
				'X-Api-Request-Source': 'Pathfinder',
			}),
		}),
		[apiHeaders]
	);

	// Stable ref callback to attach MutationObserver even if the ref mounts late
	const observerRef = useRef<MutationObserver | null>(null);
	const setContainerRef = useCallback((node: HTMLDivElement | null) => {
		containerRef.current = node;
		// If the observer exists but wasn't attached yet (ref was null on effect run),
		// attach it now that the DOM node is available
		if (node && observerRef.current) {
			observerRef.current.observe(node, { childList: true, subtree: true });
		}
	}, []);

	// -----------------------------------------------------------------------
	// CF event data accumulation
	// -----------------------------------------------------------------------
	// cfConversion fires at each step with the fields from that step.
	// - Quiz steps (element: "quiz"): fields contain accumulated quiz answers
	// - Form steps (element: "form"): fields contain contact/PII data
	// These accumulate into accumulatedFieldsRef and feed the /forms/submit
	// payload when cfSubmit fires for the form element.

	const accumulatedFieldsRef = useRef<{
		quizAnswers: Record<string, string>;
		contactFields: Record<string, string>;
		cta: { name: string; id: number } | null;
		// Field-slug → question-text, captured from the DOM while each question is
		// on screen. CF removes/replaces prior steps as the funnel advances, so the
		// text must be read at answer time, not re-scanned at submit time.
		questionLabels: Record<string, string>;
		// Slugs whose question is multi-select (data-multi-select="true"), captured
		// the same answer-time way. Only these answers may be comma-split at submit;
		// a single-select label can legitimately contain commas.
		multiSelectSlugs: Set<string>;
	}>({ quizAnswers: {}, contactFields: {}, cta: null, questionLabels: {}, multiSelectSlugs: new Set() });

	const questionCountRef = useRef(0);
	const fieldStepRef = useRef<Record<string, string>>({});

	// Email/phone for Google Ads Enhanced Conversions. Stays empty until the form is
	// submitted, so only form_submission and the post-form answers carry it.
	const enhancedConversionsEnabledRef = useRef(false);
	enhancedConversionsEnabledRef.current = isEnhancedConversionsEnabled(config?.featureFlags?.featureList);
	const enhancedConversionFieldsRef = useRef<EnhancedConversionFields>({});

	// gbraid/wbraid are only sent once the `sendGbraidIfAvailable` feature is on (DCP-14928).
	const sendGbraidRef = useRef(false);
	sendGbraidRef.current = config?.featureFlags?.featureList?.includes(SEND_GBRAID_FEATURE) ?? false;
	const captureEnhancedConversionFields = useCallback((fields: Record<string, unknown>) => {
		enhancedConversionFieldsRef.current = enhancedConversionsEnabledRef.current
			? buildEnhancedConversionFields(fields)
			: {};
		return enhancedConversionFieldsRef.current;
	}, []);

	// Metadata PUTs. Every post-submit cfAnswer fires a /metadata PUT with just
	// that one field — the backend merges by key. If an answer arrives between
	// cfSubmit and /forms/submit resolving (no leadId yet), we buffer it in
	// pendingPostFormAnswersRef and flush as a single PUT once leadInfo is set.
	const leadInfoRef = useRef<{ leadId: string; sfid: string } | null>(null);
	const formSubmittedRef = useRef(false);
	// Guards a regular (non-gated) CF quiz form from double-submitting: the
	// browser's native submit fires first, then CF's own bubble-phase handler
	// dispatches cfSubmit for the same click — both call submitForm below.
	const submitAttemptedRef = useRef(false);
	const pendingPostFormAnswersRef = useRef<Record<string, string>>({});

	const sendInsights = useCallback(
		(insights: Record<string, string>) => {
			const lead = leadInfoRef.current;
			if (!lead?.leadId) return;
			if (Object.keys(insights).length === 0) return;
			const url = `${baseUrl}/leads/${encodeURIComponent(lead.leadId)}/metadata`;
			putLeadMetadata(url, { leadUpdates: { insights } }, buildApiHeaders()).catch(() => {
				logError('ConvertFlowQuiz: /leads/{id}/metadata request failed');
			});
		},
		[baseUrl, buildApiHeaders]
	);

	// Submits the funnel to /forms/submit and reconciles lead state. Returns a
	// per-CF-field error map (keyed by `name`, `email`, ...) built from any
	// backend `failure_list`; empty on success or fail-open errors.
	//
	// When user-details validation is enabled this runs INSIDE the CF validation
	// gate (before CF advances), so a returned error blocks navigation. When it
	// is disabled it runs from the cfSubmit handler instead (fire-and-forget).
	const submitForm = useCallback(
		async (cfFields: Record<string, string>): Promise<Record<string, string>> => {
			const {
				contactFields: separatedContact,
				questionAnswers: separatedAnswers,
				marketingFields: separatedMarketing,
				privacyConsent,
			} = separateCFFields(cfFields);

			// While `sendGbraidIfAvailable` is off, no braid is sent: not even one
			// already sitting in the marketing_source cookie.
			const cookieValue = getMarketingSourceCookie();
			const marketingData = buildMarketingDataPayload({
				cookieValue: sendGbraidRef.current ? cookieValue : withoutBraidParams(cookieValue),
				pathname: globalThis.window !== undefined ? globalThis.window.location.pathname : undefined,
				referrer: globalThis.document !== undefined ? globalThis.document.referrer : undefined,
				formId: separatedMarketing.form_id,
				defaultCampaignId: separatedMarketing.default_campaign_id,
				// /forms/submit stores these as their own lead fields (DCP-15469).
				clickIds: sendGbraidRef.current ? resolveClickIdsSafely() : undefined,
			});

			// Store form_id so buildApiHeaders can gate the Pathfinder header.
			formIdRef.current = separatedMarketing.form_id ?? '';

			// /forms/submit expects leadAnswers keyed by full question text per ACs.
			// Field slugs (e.g. Missing_Teeth_c) are resolved to the question text that
			// was captured from the DOM as each question rendered (questionLabels), and
			// only multi-select answers have comma-separated values rejoined with `;`.
			const leadAnswers = buildLeadAnswers(
				{ ...accumulatedFieldsRef.current.quizAnswers, ...separatedAnswers },
				accumulatedFieldsRef.current.questionLabels,
				accumulatedFieldsRef.current.multiSelectSlugs
			);
			const payload = buildSubmitFormPayload(separatedContact, leadAnswers, marketingData);

			formSubmittedRef.current = true;
			captureEnhancedConversionFields(cfFields);
			const submitUrl = `${baseUrl}/forms/submit`;

			try {
				const result = await postFormSubmit(submitUrl, payload, buildApiHeaders());
				if (isSubmitFormSuccess(result)) {
					leadInfoRef.current = { leadId: result.lead.id, sfid: result.lead.sfid };
					// Flush any post-form answers that arrived before /forms/submit
					// resolved — they couldn't fire without a leadId.
					const buffered = pendingPostFormAnswersRef.current;
					if (Object.keys(buffered).length > 0) {
						pendingPostFormAnswersRef.current = {};
						sendInsights(remapAnswerKeys(buffered, accumulatedFieldsRef.current.questionLabels));
					}
					// Note: Consent submission requires OneTrust to be configured in the
					// BEFFE backend. This may fail locally if OneTrust credentials are not
					// set up, but should work in stage/prod.
					if (result.lead?.sfid && beffeGraphqlUrl) {
						const consentPayload = {
							Email: String(payload.leadContact.email || ''),
							Mobile: String(payload.leadContact.phone || ''),
							FirstName: String(payload.leadContact.first_name || ''),
							LastName: String(payload.leadContact.last_name || ''),
							Zip: String(payload.leadContact.zip_code || ''),
						};
						submitConsentData(
							privacyConsent,
							beffeGraphqlUrl as string,
							result.lead.sfid,
							consentPayload
						).catch(() => {
							logError(
								'ConvertFlowQuiz: consent submission failed (expected in local if OneTrust not configured)'
							);
						});
					}
					return {};
				}

				// Non-success: surface mapped backend field errors and allow a retry.
				// Drop any answers buffered during this failed attempt — they belong
				// to a lead that was never created, so flushing them onto the retry
				// lead would send stale/duplicate insights.
				formSubmittedRef.current = false;
				submitAttemptedRef.current = false;
				pendingPostFormAnswersRef.current = {};
				enhancedConversionFieldsRef.current = {};
				const fieldErrors = extractBackendFieldErrors(result, cfFields);
				const messages: Record<string, string> = {};
				for (const [key, entry] of Object.entries(fieldErrors)) {
					messages[key] = entry.message;
				}
				return messages;
			} catch {
				formSubmittedRef.current = false;
				submitAttemptedRef.current = false;
				pendingPostFormAnswersRef.current = {};
				enhancedConversionFieldsRef.current = {};
				logError('ConvertFlowQuiz: /forms/submit request failed');
				return {};
			}
		},
		[baseUrl, buildApiHeaders, beffeGraphqlUrl, sendInsights, captureEnhancedConversionFields]
	);

	useConvertFlowUserDetailsValidation({
		enabled: !!enableUserDetailsValidation,
		isSpanish: !!isSpanish,
		validateSubmission: submitForm,
	});

	useEffect(() => {
		if (!ctaId) return;
		const targetCtaId = Number(ctaId);

		// Per-question dataLayer push. ConvertFlow fires cfAnswer inconsistently
		// (it skips some quiz widget/step types entirely), but cfConversion fires
		// reliably on every advance and carries the accumulated answer set. So both
		// handlers funnel newly-answered questions through here, deduped by field
		// name so a question that fires BOTH events is only pushed once. The counter
		// increments per unique question, keeping Q<n> contiguous and correct.
		const pushedQuestionKeys = new Set<string>();

		// cfAnswer authoritatively names the field the user actually answered on a
		// given step. ConvertFlow folds look-ahead DEFAULT answers for conditional
		// questions that were never rendered into a later step's cfConversion
		// payload, so when we have a cfAnswer for a step we trust it and drop any
		// other field in that step's conversion as a phantom. These track which
		// steps cfAnswer covered and which field names it confirmed.
		const cfAnswerSteps = new Set<number>();
		const cfAnswerFields = new Set<string>();

		// CF pre-renders every quiz question (incl. never-shown conditionals) and can
		// pre-select a DEFAULT option on them, so "has a selected option" alone can't
		// tell a real terminal answer from a phantom. What CF does NOT do is display a
		// conditional question whose condition was never met. We therefore record each
		// question block the moment it becomes visible; a phantom never enters this set.
		const renderedQuizQuestions = new Set<Element>();
		const recordRenderedQuestions = () => {
			const node = containerRef.current;
			if (!node) return;
			node.querySelectorAll('.cf-quiz-question').forEach((question) => {
				if (isQuizQuestionVisible(question, node)) renderedQuizQuestions.add(question);
			});
			// Capture question text while blocks are on screen. CF removes/replaces
			// prior steps as the funnel advances, so this is the only reliable moment
			// to read the prompt for the slug -> question-text mapping used at submit.
			accumulatedFieldsRef.current.questionLabels = {
				...accumulatedFieldsRef.current.questionLabels,
				...buildQuestionTextMap(renderedQuizQuestions),
			};
			// Same answer-time window: record which slugs are multi-select so submit
			// only comma-splits those, never a single-select label containing commas.
			for (const slug of collectMultiSelectSlugs(renderedQuizQuestions)) {
				accumulatedFieldsRef.current.multiSelectSlugs.add(slug);
			}
		};

		// Quiz-funnel entry event. GTM's funnel trigger listens for form_step 'start'.
		// It must fire when the user ANSWERS THE FIRST QUESTION — not when the quiz
		// merely loads/opens. DCP-14686 corrects DCP-14512, which incorrectly emitted
		// it on the initial cfView (quiz load). cfAnswer fires inconsistently for the
		// entry question, so the first recorded Q<n> push (below) is the only reliable
		// "first question answered" signal. Guarded so it fires exactly once.
		let quizStartPushed = false;
		const emitQuizStart = (ctaName: string) => {
			if (quizStartPushed) return;
			// Never treat a post-form insight answer as the funnel entry. pushQuestionEvent
			// also runs for insight answers that arrive after cfSubmit; in a question-less
			// campaign (form-only CTA) the first field to reach it would otherwise emit
			// 'start' after 'form_submission'. formSubmittedRef is the authoritative
			// "past the form" signal used elsewhere in this file.
			if (formSubmittedRef.current) return;
			quizStartPushed = true;
			dataLayerConfig({
				event: 'formSubmission',
				...(leadInfoRef.current?.sfid && { lead_id: leadInfoRef.current.sfid }),
				form_data: {
					brand: QUIZ_BRAND,
					submission_date: new Date().toISOString(),
					form_type: 'Quiz',
					form_title: ctaName,
					form_result: 'N/A',
					form_question: '',
					form_answer: '',
					form_step: 'start',
				},
			});
		};

		const pushQuestionEvent = (
			fieldName: string,
			fieldValue: string,
			ctaName: string,
			update?: { step: string }
		) => {
			if (!fieldName) return;
			// For updates (back-navigation re-answers) bypass dedup and use the saved
			// step. For new answers enforce dedup and assign the next Q<n> step.
			if (!update && pushedQuestionKeys.has(fieldName)) return;
			let step: string;
			if (update) {
				step = update.step;
			} else {
				// A genuinely new question answer — emit the funnel entry step once,
				// immediately before the first Q1 push, so the funnel reads start -> Q1.
				emitQuizStart(ctaName);
				pushedQuestionKeys.add(fieldName);
				questionCountRef.current += 1;
				fieldStepRef.current[fieldName] = `Q${questionCountRef.current}`;
				step = `Q${questionCountRef.current}`;
				// Seed the accumulator with the first-push value so back-nav detection
				// works even when cfConversion hasn't fired for this question yet (e.g.
				// single-step quiz blocks where cfConversion fires once at the end of the
				// whole block rather than per-question auto-advance).
				accumulatedFieldsRef.current.quizAnswers = {
					...accumulatedFieldsRef.current.quizAnswers,
					[fieldName]: fieldValue,
				};
			}
			cfLog('dataLayer push', {
				form_step: step,
				form_question: fieldName,
				form_answer: fieldValue,
			});
			dataLayerConfig({
				event: 'formSubmission',
				...(leadInfoRef.current?.sfid && { lead_id: leadInfoRef.current.sfid }),
				form_data: {
					brand: QUIZ_BRAND,
					submission_date: new Date().toISOString(),
					form_type: 'Quiz',
					form_title: ctaName,
					form_result: 'N/A',
					form_question: fieldName,
					form_answer: fieldValue,
					form_step: step,
					...enhancedConversionFieldsRef.current,
				},
			});
		};

		// Pushes dataLayer events for each accepted quiz answer entry.
		// Entries whose value changed since the previous visit fire directly with
		// the original Q<n> step (back-navigation case); new answers go through
		// pushQuestionEvent which deduplicates by field name.
		const pushAcceptedAnswerEvents = (
			acceptedEntries: [string, string][],
			prevQuizAnswers: Record<string, string>,
			ctaName: string
		) => {
			for (const [key, value] of acceptedEntries) {
				const prevValue = prevQuizAnswers[key];
				if (Object.prototype.hasOwnProperty.call(prevQuizAnswers, key) && prevValue !== value) {
					const savedStep = fieldStepRef.current[key];
					if (savedStep) {
						pushQuestionEvent(key, value, ctaName, { step: savedStep });
					}
				} else {
					pushQuestionEvent(key, value, ctaName);
				}
			}
		};

		// A CF "step" can contain multiple rendered screens. The first step is a
		// multi-question quiz: each answered question re-renders the next question
		// via cfAnswer with NO step-number change. Later steps (e.g. the form)
		// arrive as cfView with an incremented step. Either kind of transition can
		// leave the new screen's top cut off behind a sticky header, so we realign
		// the snippet's top edge on both.
		const scrollQuizIntoView = () => {
			const node = containerRef.current;
			if (!node) return;
			// Offset past anything pinned to the top of the viewport (e.g. a
			// sticky nav + announcement bar) so the heading isn't hidden behind it.
			// scroll-margin-top is honored by scrollIntoView.
			const offset = getStickyHeaderOffset();
			node.style.scrollMarginTop = `${offset + 16}px`;
			const prefersReducedMotion =
				typeof window.matchMedia === 'function' &&
				window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			node.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
		};

		// Triggers feed a debounce that (a) coalesces the burst of events fired for
		// one advance (e.g. cfAnswer + cfConversion for a survey selection, or
		// cfConversion + cfView at the quiz->form boundary) into a single scroll,
		// and (b) defers it so the newly-rendered screen's DOM has settled before we
		// measure the offset. Window CF events drive most transitions; the
		// MutationObserver below covers the cases that fire no event (message-only
		// "Continue" screens and Back navigation).
		let scrollTimer: ReturnType<typeof setTimeout> | null = null;
		const scheduleScroll = () => {
			if (scrollTimer != null) clearTimeout(scrollTimer);
			scrollTimer = setTimeout(() => {
				scrollTimer = null;
				scrollQuizIntoView();
			}, 120);
		};

		// Quiz screens advance/return with NO window CF event in some cases: the
		// "Continue" on a message-only screen, and Back navigation (CF re-shows a
		// previously rendered screen by toggling visibility rather than adding
		// nodes). So we also watch the container and scroll whenever the *visible*
		// content changes. innerText reflects only visible text, so its signature
		// changes both when CF swaps in new nodes (forward) and when it un-hides an
		// existing screen (back). We skip the initial render and stop once the form
		// is reached, so the form's inputs/validation don't trigger scrolls.
		let reachedForm = false;
		let lastScreenSignature: string | null = null;
		const computeSignature = () => (containerRef.current?.innerText ?? '').trim().slice(0, 200);
		const onContainerMutated = () => {
			// Capture questions as they render — do this even after reachedForm so a
			// terminal question shown right before the form boundary is still recorded.
			recordRenderedQuestions();
			if (reachedForm) return;
			const sig = computeSignature();
			if (!sig) return;
			if (lastScreenSignature === null) {
				// First content we see is the initial render — record, don't scroll.
				lastScreenSignature = sig;
				return;
			}
			if (sig === lastScreenSignature) return;
			lastScreenSignature = sig;
			scheduleScroll();
		};
		const swapObserver = new MutationObserver(onContainerMutated);
		if (containerRef.current) {
			// If content is already present, seed the signature so the next change is
			// treated as a real navigation, not the initial render.
			if (containerRef.current.childNodes.length > 0) lastScreenSignature = computeSignature();
			// Record the first question, which is already on screen before any mutation.
			recordRenderedQuestions();
			swapObserver.observe(containerRef.current, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
			});
		}

		// cfView fires on the initial campaign view (step 1) and again on reaching
		// each subsequent step (e.g. quiz -> form). Scroll for any step past the
		// first. Tolerate a missing cta id — only bail when it's explicitly for a
		// different CTA. Note: this handler deliberately does NOT emit form_step
		// 'start' — that fires on the first answered question (see emitQuizStart),
		// not on quiz load (DCP-14686).
		const onView = (event: Event) => {
			const detail = (event as CustomEvent<CFViewDetail>).detail;
			cfLog('cfView received', { ctaId: detail?.cta?.id, targetCtaId, step: detail?.step, detail });
			if (!detail) return;
			if (detail.cta?.id != null && detail.cta.id !== targetCtaId) {
				cfLog('cfView ignored (cta mismatch)', { ctaId: detail.cta?.id, targetCtaId });
				return;
			}
			const step = typeof detail.step === 'number' ? detail.step : null;
			if (step == null) return;
			// Past the quiz (step 1) is the form step — disable the DOM-swap observer
			// there so the form's inputs/validation don't trigger scrolls. Returning
			// to step 1 re-enables it.
			reachedForm = step > 1;
			if (step <= 1) return;
			scheduleScroll();
		};

		const onConversion = (event: Event) => {
			const detail = (event as CustomEvent<CFConversionDetail>).detail;
			cfLog('cfConversion received', {
				ctaId: detail?.cta?.id,
				targetCtaId,
				element: detail?.element,
				step: detail?.step,
				fields: detail?.fields,
			});
			if (!detail) return;

			// Only process events for this CTA instance
			if (detail.cta?.id !== targetCtaId) {
				cfLog('cfConversion ignored (cta mismatch)', { ctaId: detail.cta?.id, targetCtaId });
				return;
			}

			accumulatedFieldsRef.current.cta = detail.cta ?? null;

			// Diagnostic: surface answers that arrive via cfConversion. If a quiz
			// answer shows up here but NOT via a cfAnswer event, that's the missing
			// Q<n> dataLayer push — cfConversion is the reliable per-advance signal.
			const priorQuizKeys = Object.keys(accumulatedFieldsRef.current.quizAnswers);

			// Every advance within the funnel is a conversion (incl. message-only
			// screens that fire no cfAnswer), so this is the catch-all scroll trigger.
			scheduleScroll();

			const fields = detail.fields;
			if (!fields) return;

			if (detail.element === 'quiz') {
				// Quiz step: fields are flat key/value pairs for this step. Extra
				// fields from cfAnswer are nested under fields.extra in cfAnswer, but
				// in cfConversion they're flattened.
				const { extra, ...rest } = fields;
				const extraFields = typeof extra === 'object' && extra !== null ? extra : {};
				const stepFields = { ...(rest as Record<string, string>), ...(extraFields as Record<string, string>) };

				// When cfAnswer already named the answer for this step, it's
				// authoritative: any other field here is a look-ahead default for a
				// conditional question that was never rendered, so drop it. When
				// cfAnswer was silent for the step (it fires inconsistently, e.g. the
				// entry question), the conversion is our only signal — keep everything.
				const step = typeof detail.step === 'number' ? detail.step : null;
				const cfAnswerConfirmedStep = step != null && cfAnswerSteps.has(step);
				// cfAnswer does not fire for the *terminal* question of a quiz step —
				// its selection advances the funnel via cfSubmit(quiz), so the real
				// answer would be dropped as a phantom. Recover it from the DOM, but
				// only among questions that were actually rendered on screen: a genuine
				// phantom (never-shown conditional) can still carry a pre-selected
				// default option, so we require both "was rendered" and "has a
				// selection". Capture once more here in case the terminal question is
				// still visible at conversion time. A field is kept if cfAnswer
				// confirmed it OR its rendered question shows a selection.
				recordRenderedQuestions();
				const domSelectedFields = collectSelectedQuizFields(
					renderedQuizQuestions,
					new Set(Object.keys(stepFields))
				);
				const acceptedEntries = Object.entries(stepFields).filter(
					([key]) => !cfAnswerConfirmedStep || cfAnswerFields.has(key) || domSelectedFields.has(key)
				);

				// Capture before update so we can detect answers changed after back-navigation
				const prevQuizAnswers = accumulatedFieldsRef.current.quizAnswers;

				accumulatedFieldsRef.current.quizAnswers = {
					...prevQuizAnswers,
					...Object.fromEntries(acceptedEntries),
				};
				const newKeys = Object.keys(accumulatedFieldsRef.current.quizAnswers).filter(
					(k) => !priorQuizKeys.includes(k)
				);
				cfLog('cfConversion quiz answers', {
					newlySeen: newKeys.map((k) => ({
						[k]: accumulatedFieldsRef.current.quizAnswers[k],
					})),
					droppedLookaheadDefaults: Object.keys(stepFields).filter(
						(k) => cfAnswerConfirmedStep && !cfAnswerFields.has(k) && !domSelectedFields.has(k)
					),
					totalAccumulated: accumulatedFieldsRef.current.quizAnswers,
				});
				pushAcceptedAnswerEvents(acceptedEntries, prevQuizAnswers, detail.cta?.name ?? '');
			} else if (detail.element === 'form') {
				// Form step: fields are contact/PII data
				accumulatedFieldsRef.current.contactFields = {
					...accumulatedFieldsRef.current.contactFields,
					...(fields as Record<string, string>),
				};
			}
		};

		const onAnswer = (event: Event) => {
			const detail = (event as CustomEvent<CFAnswerDetail>).detail;
			cfLog('cfAnswer received', {
				ctaId: detail?.cta?.id,
				targetCtaId,
				element: detail?.element,
				step: detail?.step,
				extra: detail?.fields?.extra,
			});
			if (!detail) return;

			// Only process events for this CTA instance
			if (detail.cta?.id !== targetCtaId) {
				cfLog('cfAnswer ignored (cta mismatch)', { ctaId: detail.cta?.id, targetCtaId });
				return;
			}

			const extra = detail.fields?.extra ?? {};
			const entries = Object.entries(extra);
			const fieldName = entries[0]?.[0] ?? '';
			const fieldValue = entries[0]?.[1] ?? '';
			const isQuizPreSubmit = detail.element === 'quiz' && !formSubmittedRef.current;

			// A quiz-element answer advances to the next question within the same
			// step; the new question renders right after, so realign it into view.
			// Skip post-submit answers (insights) — those don't change the screen.
			if (isQuizPreSubmit) scheduleScroll();

			if (isQuizPreSubmit && fieldName) {
				// Record this as the authoritative answer for its step so onConversion
				// can drop the look-ahead default fields ConvertFlow folds into the same
				// step's cfConversion payload for conditional questions never rendered.
				// Only quiz-element answers count; post-form insight answers don't.
				if (typeof detail.step === 'number') cfAnswerSteps.add(detail.step);
				cfAnswerFields.add(fieldName);

				// Auto-advance: on back-navigation + re-selection, cfConversion fires
				// before the answer click is processed so it carries the old value.
				// cfAnswer fires next with the new value but pushedQuestionKeys blocks
				// pushQuestionEvent. Detect the change here, push directly with the
				// original Q<n> step, and update the accumulator so cfConversion
				// doesn't double-push if it happens to fire again with the new value.
				const prevValue = accumulatedFieldsRef.current.quizAnswers[fieldName];
				const savedStep = fieldStepRef.current[fieldName];
				if (
					Object.prototype.hasOwnProperty.call(accumulatedFieldsRef.current.quizAnswers, fieldName) &&
					prevValue !== fieldValue &&
					savedStep
				) {
					accumulatedFieldsRef.current.quizAnswers = {
						...accumulatedFieldsRef.current.quizAnswers,
						[fieldName]: fieldValue,
					};
					pushQuestionEvent(fieldName, fieldValue, detail.cta?.name ?? '', { step: savedStep });
				}
			}

			// Post-form answers feed `leadUpdates.insights`. Fire immediately if
			// we already have a leadId; otherwise buffer until /forms/submit
			// resolves and flush from the success handler below.
			if (formSubmittedRef.current && fieldName) {
				// Capture this post-form question's text while its block is still on
				// screen, then remap. Post-form insight questions render after submit,
				// so their labels aren't in the pre-submit snapshot yet.
				recordRenderedQuestions();
				if (leadInfoRef.current?.leadId) {
					sendInsights(
						remapAnswerKeys({ [fieldName]: fieldValue }, accumulatedFieldsRef.current.questionLabels)
					);
				} else {
					pendingPostFormAnswersRef.current = {
						...pendingPostFormAnswersRef.current,
						[fieldName]: fieldValue,
					};
				}
			}

			// Push the Q<n> event through the shared, deduped path. cfConversion may
			// have already pushed this question (it's the reliable signal); if so this
			// is a no-op. cfAnswer still covers post-form insight answers, which don't
			// arrive via a quiz-element cfConversion.
			pushQuestionEvent(fieldName, fieldValue, detail.cta?.name ?? '');
		};

		const onSubmit = (event: Event) => {
			const detail = (event as CustomEvent<CFConversionDetail>).detail;
			cfLog('cfSubmit received', {
				ctaId: detail?.cta?.id,
				targetCtaId,
				element: detail?.element,
				step: detail?.step,
			});
			if (!detail) return;

			// Only process events for this CTA instance
			if (detail.cta?.id !== targetCtaId) {
				cfLog('cfSubmit ignored (cta mismatch)', { ctaId: detail.cta?.id, targetCtaId });
				return;
			}

			// Only fire for form element submissions, not quiz elements
			if (detail.element !== 'form') {
				cfLog('cfSubmit ignored (element != form)', { element: detail.element });
				return;
			}

			const submitFields = (detail.fields ?? {}) as Record<string, string>;

			dataLayerConfig({
				event: 'formSubmission',
				...(leadInfoRef.current?.sfid && { lead_id: leadInfoRef.current.sfid }),
				form_data: {
					brand: QUIZ_BRAND,
					submission_date: new Date().toISOString(),
					form_type: 'Quiz',
					form_title: detail.cta?.name ?? '',
					form_result: 'N/A',
					form_question: '',
					form_answer: '',
					form_step: 'form_submission',
					...captureEnhancedConversionFields(submitFields),
				},
			});

			// When user-details validation is enabled, /forms/submit already ran
			// inside the CF validation gate (which blocks advance on a backend
			// failure_list). cfSubmit only fires on success, so re-POSTing here
			// would create a duplicate lead. Submit from here only when the gate
			// isn't active.

			// Set the Pathfinder referral cookie synchronously here — before CF
			// fires its own page redirect (which happens before cfCompletion).
			// This ensures the cross-subdomain cookie survives the hop even when the
			// cfCompletion event never reaches its listener due to CF navigating
			// the page first.
			const submittedFormId = submitFields.form_id ?? '';
			if (PATHFINDER_FORM_IDS.has(submittedFormId)) {
				try {
					setPathfinderReferralCookie();
				} catch {
					// Cookie API unavailable (e.g. SSR)
				}
			}

			if (enableUserDetailsValidation) return;

			// Guards against a double lead POST when both native submit and
			// cfSubmit fire for the same click — whichever handler runs first
			// wins; set synchronously (not left to submitForm's own internal
			// set) so the second handler's check, even in the same tick,
			// always sees the attempt already in flight.
			if (submitAttemptedRef.current) return;
			submitAttemptedRef.current = true;

			void submitForm(submitFields);
		};

		const onCompletion = (event: Event) => {
			const detail = (event as CustomEvent<CFCompletionDetail>).detail;
			cfLog('cfCompletion received', { ctaId: detail?.cta?.id, targetCtaId });

			// Only process events for this CTA instance.
			// cta.id is optional in cfCompletion (ConvertFlow may omit it) — only
			// reject when it is explicitly present and doesn't match, same as cfView.
			if (detail?.cta?.id != null && detail.cta.id !== targetCtaId) {
				cfLog('cfCompletion ignored (cta mismatch)', { ctaId: detail?.cta?.id, targetCtaId });
				return;
			}

			// cfCompletion can fire multiple times per submission, and multiple
			// listeners may be attached; dedupe per ctaId within a short window.
			const now = Date.now();
			const lastAt = lastCompletionAtByCta.get(targetCtaId) ?? 0;
			if (now - lastAt < COMPLETION_DEDUPE_MS) {
				cfLog('cfCompletion ignored (already handled)', { targetCtaId });
				return;
			}
			lastCompletionAtByCta.set(targetCtaId, now);

			dataLayerConfig({
				event: 'formSubmission',
				...(leadInfoRef.current?.sfid && { lead_id: leadInfoRef.current.sfid }),
				form_data: {
					brand: QUIZ_BRAND,
					submission_date: new Date().toISOString(),
					form_type: 'Quiz',
					form_title: detail?.cta?.name ?? '',
					form_result: '0',
					form_question: '',
					form_answer: '',
					form_step: 'Submit Quiz',
				},
			});

			// Dispatch a DOM event so page-level code (e.g. the Next.js page
			// component) can react to quiz completion — e.g. routing the user to a
			// Pathfinder result page. We use window-level dispatch so the event
			// reaches listeners regardless of where in the tree they are registered.
			const quizAnswers = { ...accumulatedFieldsRef.current.quizAnswers };
			window.dispatchEvent(
				new CustomEvent('convertFlowQuizComplete', {
					detail: {
						ctaId: targetCtaId,
						quizAnswers,
					},
				})
			);
		};

		window.addEventListener('cfView', onView);
		window.addEventListener('cfConversion', onConversion);
		window.addEventListener('cfAnswer', onAnswer);
		window.addEventListener('cfSubmit', onSubmit);
		window.addEventListener('cfCompletion', onCompletion);

		// Native form submit — guaranteed fallback trigger.
		//
		// Some CTA button configurations navigate the browser directly (e.g. a
		// "Go to URL" action) without CF ever dispatching its own cfSubmit custom
		// event, silently skipping the backend lead POST while the user still
		// advances. A real <form> submit fires this native event first, so it's a
		// reliable last-resort trigger, guarded the same way as cfSubmit so the
		// two never double-submit when both fire.
		const onNativeFormSubmit = (event: Event) => {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) return;
			if (!form.classList.contains('cf-form')) return;
			if (!containerRef.current?.contains(form)) return;

			// If CF's own handler doesn't call preventDefault (the exact
			// misconfiguration this fallback exists for), the browser's default
			// GET-and-navigate would otherwise unload the page and abort the POST.
			event.preventDefault();

			if (enableUserDetailsValidation) return;

			// See the matching guard in onSubmit above.
			if (submitAttemptedRef.current) return;
			submitAttemptedRef.current = true;

			void submitForm(readSubmitFields(form));
		};
		window.addEventListener('submit', onNativeFormSubmit, true);

		// Debug-only: also listen for CF events we DON'T currently handle, so we can
		// see whether ConvertFlow fires some other event per question (e.g. cfChange)
		// where cfAnswer is silent. Purely diagnostic — no behavior attached.
		const extraDebugEvents = [
			'cfReady',
			'cfStart',
			'cfChange',
			'cfClose',
			'cfComplete',
			'cfStep',
			'cfNext',
			'cfPrevious',
			'cfFormSubmit',
		];
		const onExtraDebug = (event: Event) => {
			const detail = (event as CustomEvent).detail;
			cfLog(`(unhandled) ${event.type} received`, {
				ctaId: detail?.cta?.id,
				targetCtaId,
				element: detail?.element,
				step: detail?.step,
				detail,
			});
		};
		if (CF_DEBUG) {
			for (const name of extraDebugEvents) window.addEventListener(name, onExtraDebug);
		}

		return () => {
			window.removeEventListener('cfView', onView);
			window.removeEventListener('cfConversion', onConversion);
			window.removeEventListener('cfAnswer', onAnswer);
			window.removeEventListener('cfSubmit', onSubmit);
			window.removeEventListener('cfCompletion', onCompletion);
			window.removeEventListener('submit', onNativeFormSubmit, true);
			if (CF_DEBUG) {
				for (const name of extraDebugEvents) window.removeEventListener(name, onExtraDebug);
			}

			swapObserver.disconnect();
			if (scrollTimer != null) clearTimeout(scrollTimer);

			// Reset all accumulated funnel state so a re-bind (e.g. ctaId change)
			// or remount starts fresh instead of replaying a prior funnel.
			accumulatedFieldsRef.current = {
				quizAnswers: {},
				contactFields: {},
				cta: null,
				questionLabels: {},
				multiSelectSlugs: new Set(),
			};
			questionCountRef.current = 0;
			fieldStepRef.current = {};
			leadInfoRef.current = null;
			formSubmittedRef.current = false;
			submitAttemptedRef.current = false;
			pendingPostFormAnswersRef.current = {};
			enhancedConversionFieldsRef.current = {};
		};
	}, [ctaId, enableUserDetailsValidation, submitForm, sendInsights, captureEnhancedConversionFields]);

	// -----------------------------------------------------------------------
	// Dev/stage simulation — NOT active in production
	// -----------------------------------------------------------------------
	// Append ?cfQuizSimulate=call or ?cfQuizSimulate=candidate to any page URL
	// to fire a synthetic convertFlowQuizComplete event and test the Pathfinder
	// routing without the ConvertFlow widget loading.
	// Requires an explicit URL param, so a real visitor can never trigger it.

	useEffect(() => {
		if (globalThis.window === undefined) return;

		const params = new URLSearchParams(globalThis.location.search);
		const simulate = params.get('cfQuizSimulate');
		if (!simulate) return;

		const hostname = globalThis.location.hostname;
		const isProdHostname = hostname === 'www.clearchoice.com' || hostname === 'www.aspendental.com';
		if (isProdHostname) return;

		const quizAnswers: Record<string, string> =
			simulate === 'call' ? { Credit_Score_c: 'Under 660' } : { Credit_Score_c: '740+' };

		cfLog('cfQuizSimulate triggered', { simulate, quizAnswers });

		globalThis.dispatchEvent(
			new CustomEvent('convertFlowQuizComplete', {
				detail: { ctaId: Number(ctaId), quizAnswers },
			})
		);
	}, [ctaId]);

	// -----------------------------------------------------------------------
	// Script loading
	// -----------------------------------------------------------------------

	useEffect(() => {
		if (!websiteId || !ctaId) {
			setState('error');
			return;
		}

		let cancelled = false;

		// Watch the snippet div for ConvertFlow injecting content
		const observer = new MutationObserver(() => {
			if (containerRef.current && containerRef.current.childNodes.length > 0) {
				if (!cancelled) setState('ready');
				observer.disconnect();
			}
		});
		observerRef.current = observer;

		// Attach immediately if the ref is already available
		if (containerRef.current) {
			observer.observe(containerRef.current, { childList: true, subtree: true });
		}

		// Timeout fallback
		const timer = setTimeout(() => {
			if (!cancelled) {
				observer.disconnect();
				setState((prev) => (prev === 'loading' ? 'error' : prev));
			}
		}, resolvedTimeoutMs);

		// Load the script — ConvertFlow auto-renders into the snippet div
		loadConvertFlowScript(websiteId)
			.then(() => {
				if (window.convertflow) {
					// Only call load() on re-mounts when CF is fully initialized.
					// On first load, CF handles its own initialization async (including
					// loading jQuery). Calling load() too early crashes with "$cf is not defined".
					try {
						window.convertflow.load();
					} catch (err) {
						// Expected on first load — $cf (jQuery) not ready yet.
						// ConvertFlow will auto-discover snippets once fully initialized.
						// Log unexpected errors for debugging
						if (err instanceof Error && !err.message.includes('$cf')) {
							console.warn('[ConvertFlow] Unexpected error calling load():', err);
						}
					}
				}
				return undefined;
			})
			.catch(() => {
				if (!cancelled) setState('error');
			});

		return () => {
			cancelled = true;
			observerRef.current = null;
			clearTimeout(timer);
			observer.disconnect();
		};
	}, [ctaId, websiteId, resolvedTimeoutMs]);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<Box component="section" aria-label="Interactive quiz" sx={{ width: '100%' }}>
			{/* Status region announced to screen readers on state change */}
			<Box aria-live="polite" aria-atomic="true">
				{/* Loading skeleton — shown until CF is ready */}
				{state === 'loading' && <QuizSkeleton message={resolvedLoadingMessage} />}

				{/* Error / timeout fallback */}
				{state === 'error' && (
					<QuizFallback fallbackUrl={fallbackUrl ?? undefined} fallbackLabel={fallbackLabel ?? undefined} />
				)}
			</Box>

			{/*
				ConvertFlow mount target.
				Hidden during loading to prevent unstyled content flash.
				Removed from layout on error so late-arriving CF content
				doesn't appear below the fallback alert.
			*/}
			{state !== 'error' && (
				<div
					ref={setContainerRef}
					className={ctaId ? `cf-cta-snippet cta${ctaId}` : undefined}
					style={state === 'loading' ? { opacity: 0, minHeight: 1 } : undefined}
					data-website-id={websiteId || undefined}
					data-cta-id={ctaId || undefined}
				/>
			)}
		</Box>
	);
}

export default ConvertFlowQuiz;
